import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { txToast } from "@/components/TxToaster";
import { useBackendApi } from "@/hooks/fetch";
import type { AdminGroup, AdminListItem, AdminSetGroupsReq } from "@shared/adminApiTypes";
import type { GenericApiOkResp } from "@shared/genericApiTypes";
import { useState } from "react";

type Props = {
    admin: AdminListItem;
    groups: AdminGroup[];
    onClose: () => void;
    onSaved: () => void;
};

export default function AdminGroupsDialog({ admin, groups, onClose, onSaved }: Props) {
    const [selected, setSelected] = useState<string[]>(admin.groups ?? []);
    const [isSaving, setIsSaving] = useState(false);

    const saveApi = useBackendApi<GenericApiOkResp, AdminSetGroupsReq>({
        method: 'POST',
        path: '/adminManager/setAdminGroups',
    });

    const toggle = (id: string, checked: boolean) => {
        setSelected((prev) => checked ? [...new Set([...prev, id])] : prev.filter((g) => g !== id));
    };

    const handleSave = () => {
        setIsSaving(true);
        saveApi({
            data: { name: admin.name, groups: selected },
            toastLoadingMessage: 'Gruppen werden gespeichert...',
            success: (data, toastId) => {
                if ('error' in data) {
                    txToast.error(data.error, { id: toastId });
                    return;
                }
                txToast.success('Gruppen gespeichert.', { id: toastId });
                onSaved();
            },
            finally: () => setIsSaving(false),
        });
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Gruppen von {admin.name}</DialogTitle>
                </DialogHeader>

                {groups.length ? (
                    <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-2 -mr-2">
                        {groups.map((group) => (
                            <label
                                key={group.id}
                                className="plate flex items-center gap-2 p-2 cursor-pointer select-none"
                            >
                                <Checkbox
                                    checked={selected.includes(group.id)}
                                    disabled={isSaving}
                                    onCheckedChange={(checked) => toggle(group.id, checked === true)}
                                />
                                <span className="truncate-flex grow">{group.name}</span>
                                <span className="text-label text-faint shrink-0">
                                    {group.permissions.includes('all_permissions')
                                        ? 'alle Berechtigungen'
                                        : `${group.permissions.length} Berechtigungen`}
                                </span>
                            </label>
                        ))}
                    </div>
                ) : (
                    <p className="text-muted-foreground text-sm">
                        Noch keine Gruppen. Leg zuerst eine im Tab „Gruppen“ an.
                    </p>
                )}

                <DialogFooter>
                    <Button variant="muted" onClick={onClose} disabled={isSaving}>Abbrechen</Button>
                    <Button variant="solid" onClick={handleSave} disabled={isSaving || !groups.length}>Speichern</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
